<script setup lang="ts">
import { Notification } from '@beeboard/common';
import Popover from 'primevue/popover';
import { RouteLocationRaw } from 'vue-router';
import NewsPanel from '@/app/common/NewsPanel.vue';
import { resolveEntityNavigationContext } from '@/app/project/catchUp/utils/entityNavigation';
import MapHeader from '@/app/remap/components/MapHeader.vue';
import { useAIAssistantContext } from '@/composables/useAIAssistantContext';
import { useNotificationFormatter } from '@/composables/useNotificationFormatter';
import { useAiAssistantStore } from '@/store/aiAssistantStore';
import { useAuthStore } from '@/store/authStore';
import { useNotificationStore } from '@/store/notificationStore';
import { useMapStore } from '@/store/remapStore';
import { useUiStore } from '@/store/uiStore';
import { IllustrationCloudSearch } from '@/ui/icons';
import { useAlerts } from '@/utils/helpers/alerts';
import { toSmartTime } from '@/utils/helpers/various';
import type { MapContext } from '@/store/remapStore/remapStore';

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();
const notificationStore = useNotificationStore();
const notificationFormatter = useNotificationFormatter();
const message = useAlerts();
const popover = ref();
const uiStore = useUiStore();
const mapStore = useMapStore();

// The assistant of a project/template page; its panel docks next to the
// page (WI #5769, App.vue).
const {
  mode: aiMode,
  contextId: aiContextId,
  isAvailable: isAIAssistantAvailable,
} = useAIAssistantContext();

// An answer of this project/template is running — also with the chat panel
// closed (WI #5766).
const aiAssistantStore = useAiAssistantStore();
const aiAnswerRunning = computed(() =>
  aiMode.value && aiContextId.value
    ? aiAssistantStore.isRunning(aiMode.value, aiContextId.value)
    : false,
);
// An answer here waits for the user to allow changes (WI #5768).
const aiDecisionPending = computed(() =>
  aiMode.value && aiContextId.value
    ? aiAssistantStore.isAwaitingDecision(aiMode.value, aiContextId.value)
    : false,
);

// A page loaded while an answer ran here (a reload, another tab) rejoins it
// with the dock closed, for the running indicator and the refresh at its end.
watch(
  () => (isAIAssistantAvailable.value ? aiContextId.value : null),
  (contextId) => {
    if (contextId && aiMode.value) {
      aiAssistantStore.rejoinRunningAnswer(aiMode.value, contextId);
    }
  },
  { immediate: true },
);

const notifications = computed(() => notificationStore.notifications);
const notificationsUnreadCount = computed(
  () => notifications.value.filter((n) => !n.isRead).length,
);

// Smart-time labels/tooltips per notification, computed once per list change
// so `toSmartTime` is not re-evaluated twice per item in the template.
const notificationTimes = computed(
  () =>
    new Map(notifications.value.map((n) => [n.id, toSmartTime(n.created_at)])),
);

const toggle = (event: MouseEvent) => {
  popover.value.toggle(event);
};

function markNotificationsAsRead() {
  notificationStore.markAllNotificationsAsRead().catch(message.error);
}

function markNotificationAsRead(id: string) {
  notificationStore.markNotificationAsRead(id).catch(message.error);
}

function getNotificationEntityLink(
  notification: Notification,
): RouteLocationRaw {
  switch (notification.entityType) {
    case 'project':
      return {
        name: 'ProjectPage',
        params: { projectId: notification.entityId },
      };
    case 'function':
      return {
        name: 'ProjectSettingsFunctions',
        params: { projectId: notification.projectId },
      };
    case 'milestone':
      return {
        name: 'ProjectMilestoneDetailPage',
        params: {
          projectId: notification.projectId,
          milestoneId: notification.entityId,
        },
      };
    case 'activity':
      return {
        name: 'ProjectActivityDetail',
        params: {
          projectId: notification.projectId,
          activityId: notification.entityId,
        },
      };
    case 'completionCriterion':
      return {
        name: 'ProjectCriterion',
        params: {
          projectId: notification.projectId,
          activityId: notification.parentEntityId,
          completionCriterionId: notification.entityId,
        },
      };
    case 'action':
      return {
        name: 'ProjectActivityAction',
        params: {
          projectId: notification.projectId,
          activityId: notification.parentEntityId,
          actionId: notification.entityId,
        },
      };
    case 'meeting':
      return {
        name: 'ProjectMeetings',
        params: {
          projectId: notification.projectId,
        },
        query: {
          meetingId: notification.entityId,
        },
      };
    case 'projectMail':
      // Mail has no page of its own: the map opens its import sidebar on this param,
      // exactly as the email and Teams links already do.
      return {
        name: 'ProjectPage',
        params: {
          projectId: notification.projectId,
        },
        query: {
          projectMailId: notification.entityId,
        },
      };
    default:
      return {
        name: 'ProjectPage',
        params: { projectId: notification.projectId },
      };
  }
}

interface MapContextbarSelection {
  context: MapContext;
  itemId: string;
  taskId?: string;
  taskType?: string;
}

// Notification entity types use camelCase; the map navigation resolver expects
// the PascalCase entity names. Only these types have a map-contextbar target;
// project/function/meeting always navigate to their detail page.
const NOTIFICATION_MAP_ENTITY_TYPES: Partial<
  Record<Notification['entityType'], string>
> = {
  activity: 'Activity',
  milestone: 'Milestone',
  completionCriterion: 'CompletionCriterion',
  action: 'Action',
};

// Resolve a notification to the map element to select in the contextbar, or
// null when the entity type has no contextbar equivalent (navigate instead).
function getMapContextbarSelection(
  notification: Notification,
): MapContextbarSelection | null {
  const entityType = NOTIFICATION_MAP_ENTITY_TYPES[notification.entityType];
  if (!entityType) {
    return null;
  }

  // Completion criteria and actions open their parent activity (with the task
  // selected); pass the parent so the resolver targets the activity.
  const { context, itemId, taskId, taskType } = resolveEntityNavigationContext(
    entityType,
    notification.entityId,
    notification.parentEntityId ? 'Activity' : null,
    notification.parentEntityId ?? null,
  );

  return context ? { context, itemId, taskId, taskType } : null;
}

// Select the element in the map contextbar and sync the deep-link query params
// (mirrors MapTaskList / catchUp entity navigation).
async function selectEntityInContextbar(selection: MapContextbarSelection) {
  // mapSelectItem toggles selection off when the same item is re-selected; a
  // notification click should always show the element, so skip re-selecting an
  // already-open item and just (re)apply the task selection below.
  const alreadySelected =
    mapStore.selectedContext === selection.context &&
    mapStore.selectedId === selection.itemId;
  if (!alreadySelected) {
    await mapStore.mapSelectItem(selection.context, selection.itemId);
  }

  if (selection.taskId && selection.taskType) {
    mapStore.selectedTaskId = selection.taskId;
    mapStore.selectedTaskType = selection.taskType;
  } else if (alreadySelected) {
    // Re-selecting a top-level entity (no task) that is already open: clear any
    // lingering child task selection so the contextbar returns to its top-level
    // view instead of staying on the CC/action tab.
    mapStore.selectedTaskId = '';
    mapStore.selectedTaskType = '';
  }

  const query = { ...route.query };
  delete query.selectedActivityId;
  delete query.selectedCompletionCriterionId;
  delete query.selectedActionId;

  // Only the activity context is deep-link restorable: RemapPage's
  // selectedContext watcher (RemapPage.vue:95-105) clears the whole query for
  // any non-'activity' context. So a milestone selection is intentionally NOT
  // written to the query — the contextbar still shows it (store state persists),
  // it just isn't restorable from the URL.
  if (selection.context === 'activity') {
    query.selectedActivityId = selection.itemId;
    if (selection.taskType === 'cc' && selection.taskId) {
      query.selectedCompletionCriterionId = selection.taskId;
    } else if (selection.taskType === 'action' && selection.taskId) {
      query.selectedActionId = selection.taskId;
    }
  }

  await router.replace({ path: route.path, query });
}

async function onNotificationClick(
  event: MouseEvent,
  notification: Notification,
  navigate: (e?: MouseEvent) => void,
) {
  // Clicking a notification marks it read regardless of how it is opened
  // (in-tab navigation, map contextbar, or a modified-click new tab).
  markNotificationAsRead(notification.id);

  // Let the browser handle modified clicks so Ctrl/Cmd/middle-click still opens
  // the detail page in a new tab.
  if (
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  ) {
    return;
  }

  const onSameProjectMap =
    route.name === 'ProjectPage' &&
    route.params.projectId === notification.projectId;
  const selection = onSameProjectMap
    ? getMapContextbarSelection(notification)
    : null;

  if (selection) {
    // On the same project's map, show the element in the contextbar instead of
    // navigating to its detail page.
    event.preventDefault();
    await selectEntityInContextbar(selection);
  } else {
    // Otherwise keep today's behavior: navigate to the detail page.
    navigate(event);
  }

  popover.value?.hide();
}

function loadNotifications() {
  return notificationStore.loadNotifications();
}

async function navigateToEmailSettings() {
  await router.push({ name: 'UserSettingsPage' });
  popover.value.hide();
}

async function navigateToRoadmap() {
  await router.push({ name: 'RoadmapPage' });
}
</script>

<template>
  <div class="top-navigation">
    <UiBreadcrumbs />
    <UiSpinner :spin="authStore.isAuthenticating" />
    <MapHeader
      v-if="
        route.name === 'ProjectMapTemplateMap' || route.name === 'ProjectPage'
      "
    />
    <div class="notifications-newsfeed">
      <UiButton variant="flat" class="notification-button" @click="toggle">
        <UiIcon name="Bell" class="icon-size-sm" />
        <span v-if="notificationsUnreadCount > 0" class="unread-badge"
          ><span v-if="notificationsUnreadCount <= 9">{{
            notificationsUnreadCount
          }}</span></span
        >
      </UiButton>

      <NewsPanel />
      <div id="help-widget-header" />

      <UiButton
        variant="flat"
        class="notification-button"
        @click="navigateToRoadmap"
      >
        <UiIcon name="BeeRoadMap" class="icon-size-sm" />
      </UiButton>

      <UiButton
        v-if="isAIAssistantAvailable"
        id="ai-assistant-button"
        variant="flat"
        class="notification-button"
        :class="{ 'is-ai-panel-open': uiStore.aiAssistantOpen }"
        :title="
          aiDecisionPending
            ? $t('ai.chat.writeAccess.waiting')
            : aiAnswerRunning
              ? $t('ai.chat.running')
              : $t('ai.panel.title')
        "
        :aria-label="$t('ai.panel.title')"
        :aria-expanded="uiStore.aiAssistantOpen"
        data-testid="ai-assistant-button"
        @click="uiStore.toggleAIAssistant()"
      >
        <UiIcon name="Sparkles" class="icon-size-sm" />
        <span
          v-if="aiDecisionPending"
          class="ai-decision-badge"
          data-testid="ai-decision-badge"
        />
        <span
          v-else-if="aiAnswerRunning"
          class="ai-running-badge"
          data-testid="ai-running-badge"
        />
      </UiButton>
    </div>

    <Popover
      ref="popover"
      :pt="{
        content: { style: { padding: '0' } },
      }"
      @show="loadNotifications"
    >
      <div v-if="notifications.length > 0" class="mark-read-button">
        <UiButton
          variant="flat"
          size="medium"
          :disabled="notificationsUnreadCount === 0"
          @click="markNotificationsAsRead"
        >
          {{ $t('notifications.markAllAsRead') }}
        </UiButton>
      </div>
      <div class="notifications-container">
        <div v-if="notifications.length === 0" id="empty-state" class="empty">
          <IllustrationCloudSearch />
          <p>{{ $t('notifications.empty') }}</p>
        </div>
        <div
          v-for="notif in notifications"
          :key="notif.id"
          class="notification-item"
          :class="{ 'notification-unread': !notif.isRead }"
        >
          <!-- eslint-disable vue/no-v-html -->
          <div
            v-if="notif.entityTitle === '[deleted]'"
            class="notification-content"
          >
            <div
              v-html="
                notificationFormatter.formatNotification(
                  notif,
                  $t(`notificationActions.${notif.action}`, {
                    dispatcher: notificationFormatter.getDispatcherName(notif),
                    entity: notificationFormatter.getEntityTitle(notif),
                    parentEntity:
                      notificationFormatter.getParentEntityTitle(notif),
                  }),
                )
              "
            />
            <div v-if="notif.projectTitle" class="notification-project">
              {{ notif.projectTitle }}
            </div>
          </div>
          <router-link
            v-else
            v-slot="{ href, navigate }"
            :to="getNotificationEntityLink(notif)"
            custom
          >
            <a
              :href="href"
              class="notification-content notification-clickable"
              @click="onNotificationClick($event, notif, navigate)"
            >
              <div>
                <div
                  v-html="
                    notificationFormatter.formatNotification(
                      notif,
                      $t(`notificationActions.${notif.action}`, {
                        dispatcher:
                          notificationFormatter.getDispatcherName(notif),
                        entity: notificationFormatter.getEntityTitle(notif),
                        parentEntity:
                          notificationFormatter.getParentEntityTitle(notif),
                      }),
                      true,
                    )
                  "
                />
                <div v-if="notif.projectTitle" class="notification-project">
                  {{ notif.projectTitle }}
                </div>
              </div>
            </a>
          </router-link>
          <!-- eslint-enable vue/no-v-html -->
          <div class="notification-meta">
            <span
              class="notification-date"
              :title="notificationTimes.get(notif.id)?.tooltip"
            >
              {{ notificationTimes.get(notif.id)?.label }}
            </span>
            <button
              v-if="!notif.isRead"
              type="button"
              class="notification-unread-dot"
              :title="$t('notifications.markAsRead')"
              :aria-label="$t('notifications.markAsRead')"
              @click.stop.prevent="markNotificationAsRead(notif.id)"
            />
          </div>
        </div>
      </div>
      <div class="email-settings-link">
        <UiButton
          id="notifications-settings-header"
          variant="flat"
          @click="navigateToEmailSettings"
        >
          <UiIcon name="Settings" class="icon-size-sm" />
          {{ $t('notifications.settings') }}
        </UiButton>
      </div>
    </Popover>
  </div>
</template>

<style scoped lang="scss">
/* The AI assistant's panel is docked next to the page (WI #5769). */
.ui-button.flat.is-ai-panel-open {
  background-color: var(--color-primary-50);
  color: var(--color-primary-700);
}

/* An answer waits for the user's decision on changes (WI #5768). */
.ai-decision-badge {
  position: absolute;
  top: 3px;
  right: 3px;
  z-index: 1;
  width: 9px;
  height: 9px;
  border: 2px solid white;
  border-radius: var(--border-radius-circle);
  background-color: var(--color-warning-500, #f59e0b);
}

.ai-running-badge {
  position: absolute;
  top: 4px;
  right: 4px;
  z-index: 1;
  width: 8px;
  height: 8px;
  border-radius: var(--border-radius-circle);
  background-color: var(--color-primary-500);
  animation: ai-running-pulse 1.4s ease-in-out infinite;
}

@keyframes ai-running-pulse {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.4;
    transform: scale(0.75);
  }
}

.unread-badge {
  position: absolute;
  top: 2px;
  right: 2px;
  z-index: 1;
  background-color: var(--color-error);
  color: white;
  border-radius: var(--border-radius-circle);
  padding: 0;
  font-size: var(--font-size-10);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  line-height: 14px; /* Ensure text is vertically centered */
}

.unread-badge span {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.notifications-newsfeed {
  display: flex;
  align-items: center;
  gap: var(--space-4);
}

/* Teleport target for the help widget. When the widget is floating this stays
   empty; display: contents keeps it from occupying a flex gap slot (which would
   double the gap between NewsPanel and the Roadmap button). */
#help-widget-header {
  display: contents;
}

.empty {
  margin-top: px(28);
  display: flex;
  flex-direction: column;
  align-items: center;
  color: var(--color-gray-500);
  row-gap: var(--space-12);
}

.mark-read-button {
  display: flex;
  width: 100%;
  justify-content: flex-end;
  margin-top: var(--space-10);
  margin-bottom: px(-8);
}

.notifications-container {
  width: 500px;
  height: 400px;
  max-height: 300px;
  padding: var(--space-10) px(0);
  overflow-y: auto;
}
.notification-item {
  font-size: var(--font-size-12);
  min-height: px(55);
  display: flex;
  align-items: center;
  padding: px(5) var(--space-10);
  gap: var(--space-10);
  border-top: 1px solid var(--color-gray-200);
  &:last-of-type {
    border-bottom: 1px solid var(--color-gray-200);
  }
}
.notification-unread {
  background-color: rgba(var(--color-primary-50--rgb), 0.6);
}
.notification-content {
  width: 100%;
}
.notification-meta {
  flex-shrink: 0;
  align-self: flex-start;
  display: flex;
  align-items: center;
  gap: var(--space-8);
  white-space: nowrap;
}
.notification-date {
  font-size: var(--font-size-11);
  color: var(--color-gray-500);
}
.notification-unread-dot {
  flex-shrink: 0;
  width: px(10);
  height: px(10);
  padding: 0;
  border: none;
  border-radius: var(--border-radius-circle);
  background-color: var(--color-primary-500);
  cursor: pointer;

  &:hover {
    background-color: var(--color-primary-700);
  }
}
.notification-clickable {
  cursor: pointer;

  &:hover .entity-clickable {
    text-decoration: underline;
  }
}
:deep(.dispatcher-name) {
  font-weight: var(--font-weight-bold);
}
:deep(.entity-details) {
  font-weight: var(--font-weight-bold);
  color: var(--color-primary-500);
}
:deep(.entity-link) {
  color: var(--color-primary-600);
}
:deep(.entity-clickable) {
  text-decoration: none;
}
:deep(.entity-deleted) {
  text-decoration: line-through;
}
:deep(.parent-entity-name) {
  font-weight: var(--font-weight-bold);
  color: var(--color-gray-700);
}
.notification-project {
  font-size: var(--font-size-11);
  color: var(--color-gray-600);
  margin-top: var(--space-4);
  font-style: italic;
}
.email-settings-link {
  width: 100%;
  border-top: 1px solid var(--color-gray-200);
  background-color: var(--color-gray-50);
}
</style>
